<ng-container *transloco="let t; read: 'project.project-details-item-panel'">
  @if (selectedItem) {
    <div class="project-panel__container">
      @if (isLoading) {
        <div class="xi-loader-container__inline-overlay xi-loader-container__inline-overlay--blur">
          <xi-animation [loop]="true" animation="cx_loader"></xi-animation>
        </div>
      }
      <div class="project-panel__header-container">
        <div class="project-panel__header">
          <span
            [class.adjusted-width]="
              selectedItem.type === 'analysis' && selectedItem.analysis?.primary
            "
          >
            <h2 class="kxi-text-medium kxi-text-600">{{ selectedItem.name }}</h2>
            @if (selectedItem.type === 'analysis' && selectedItem.analysis?.primary) {
              <xi-icon
                icon="star_filled"
                [tooltipProps]="{
                  text: t('primary'),
                  direction: 12,
                }"
                iconSize="large"
              ></xi-icon>
            }
          </span>

          <xi-icon
            [icon]="icons[selectedItem.isSocial ? 'socialCollection' : selectedItem.type].icon"
            [color]="icons[selectedItem.isSocial ? 'socialCollection' : selectedItem.type].color"
            iconSize="xxlarge"
          ></xi-icon>

          @if (selectedItem?.group?.collectionQuery) {
            <xi-icon
              [icon]="icons['dynamicGroup'].icon"
              iconSize="small"
              class="dynamic-group"
              [color]="icons['dynamicGroup'].color"
            ></xi-icon>
          }
        </div>
      </div>

      @if (!isLoading) {
        <div>
          <div class="project-panel__content">
            <div class="project-panel__content-item-wrapper project-panel__notes">
              <div class="project-panel__notes-header">
                <h3 class="kxi-text-normal kxi-text-600">{{ t('notes') }}</h3>

                <xi-icon-button
                  class="project-panel__notes-edit-btn"
                  icon="edit"
                  fill="no-fill"
                  (onClick)="onOpenNotesEditor()"
                >
                </xi-icon-button>
              </div>

              <p
                class="project-panel__notes-content"
                [class.project-panel__notes-content--empty]="!note"
              >
                {{ note ?? t('no-notes') }}
              </p>
            </div>

            @switch (selectedItem.type) {
              @case ('collection') {
                <ng-container
                  *ngTemplateOutlet="tCollection; context: { $implicit: selectedItem }"
                />
              }
              @case ('group') {
                <ng-container *ngTemplateOutlet="tGroup; context: { $implicit: selectedItem }" />
              }
              @case ('analysis') {
                @if (selectedItem.displayData.isDriverAnalysis) {
                  <ng-container
                    *ngTemplateOutlet="tKeyDriverAnalysis; context: { $implicit: selectedItem }"
                  />
                } @else {
                  <ng-container *ngTemplateOutlet="analysisOutlet"></ng-container>
                }
              }
              @case ('dashboard') {
                <ng-container
                  *ngTemplateOutlet="tDashboard; context: { $implicit: selectedItem }"
                ></ng-container>
              }
            }
            <ng-template #analysisOutlet>
              <ng-container
                *ngTemplateOutlet="tAnalysis; context: { $implicit: selectedItem }"
              ></ng-container>
            </ng-template>
          </div>
        </div>
      }
    </div>
  } @else {
    <app-project-about [itemToShow]="currentTabId"></app-project-about>
  }

  <ng-template #tCollection let-collectionItem>
    <div class="project-panel__content-item-wrapper">
      <h3 class="kxi-text-normal kxi-text-600">{{ t('origin') }}</h3>
      @for (origin of collectionItem.collection.data.origin; track origin) {
        <p>{{ origin }}</p>
      }
      @if (!collectionItem.collection.data.origin[0]) {
        <p>{{ t('unspecified') }}</p>
      }
    </div>

    @if (
      collectionItem.displayData.isDriverAnalysis && collectionItem.collection.driverAnalyses.length
    ) {
      <div class="project-panel__content-item-wrapper">
        <h3 class="project-panel__content-pill-header">
          {{ t('key-driver-analyses') }} ({{ collectionItem.collection.driverAnalyses.length }})
        </h3>

        @for (kda of collectionItem.collection.driverAnalyses; track kda.id) {
          <div
            [matTooltip]="kda.displayName"
            (click)="onKDAClicked(kda)"
            class="project-panel__pill"
          >
            <xi-icon
              [icon]="icons.analysis.icon"
              [color]="icons.analysis.color"
              iconSize="normal"
            ></xi-icon>

            <span>{{ kda.displayName | truncateEnd: 25 }}</span>
          </div>
        }
      </div>
    }

    @if (collectionItem.displayData.analysisCount > 0) {
      <div class="project-panel__content-item-wrapper">
        <h3 class="project-panel__content-pill-header">
          {{ t('nlp-analyses') }} ({{ collectionItem.displayData.analysisCount }})
        </h3>

        @for (analysis of collectionItem.collection.analyses; track analysis.name) {
          <div
            [matTooltip]="analysis.displayName"
            (click)="onAnalysisClicked(analysis)"
            class="project-panel__pill"
          >
            <xi-icon
              [icon]="icons.analysis.icon"
              [color]="icons.analysis.color"
              iconSize="normal"
            ></xi-icon>
            <span>{{ analysis.displayName | truncateEnd: 25 }}</span>
          </div>
        }
      </div>
    }

    @if (!collectionItem.isSocial) {
      <ng-container
        *ngTemplateOutlet="tActiveUse; context: { $implicit: collectionItem.displayData }"
      />
    }

    @let name = collectionItem.displayData.smartSummaryName;
    <!-- Smart Summary -->
    @if (name) {
      <div class="project-panel__content-item-wrapper">
        <h3 class="kxi-text-normal kxi-text-600">{{ t('smart-summary') }}</h3>
        <p matTooltip="{{ name }}" [matTooltipDisabled]="!(name.length >= 40)">
          {{ name | truncate: 40 }}
        </p>
      </div>
    }

    <ng-container
      *ngTemplateOutlet="tLabels; context: { $implicit: collectionItem }"
    ></ng-container>
  </ng-template>

  <ng-template #tGroup let-groupItem>
    @if (groupItem.group.collectionQuery) {
      <div class="project-panel__content-item-wrapper">
        <h3 class="kxi-text-normal kxi-text-600">{{ t('collection-query') }}</h3>
        <p>{{ groupItem.group.collectionQuery }}</p>
      </div>
    }

    @if (groupItem.displayData.analysisCount > 0) {
      <div class="project-panel__content-item-wrapper">
        <h3 class="project-panel__content-pill-header">
          {{ t('analyses') }} ({{ groupItem.displayData.analysisCount }})
        </h3>

        @for (analysis of groupItem.group.analyses; track analysis.name) {
          <div
            [matTooltip]="analysis.displayName"
            (click)="onAnalysisClicked(analysis)"
            class="project-panel__pill"
          >
            <xi-icon
              [icon]="icons.analysis.icon"
              [color]="icons.analysis.color"
              iconSize="normal"
            ></xi-icon>
            <span>{{ analysis.displayName | truncateEnd: 25 }}</span>
          </div>
        }
      </div>
    }

    <ng-container
      *ngTemplateOutlet="tActiveUse; context: { $implicit: groupItem.displayData }"
    ></ng-container>

    <ng-container *ngTemplateOutlet="tLabels; context: { $implicit: groupItem }"></ng-container>
  </ng-template>

  <ng-template #tAnalysis let-analysisItem>
    @if (currentTabId !== 'group') {
      <div class="project-panel__content-item-wrapper">
        <h3 class="kxi-text-normal kxi-text-600">{{ t('total-doc-count') }}</h3>
        <p>{{ analysisItem.displayData.docCount | number }}</p>
      </div>
    }

    <div class="project-panel__content-item-wrapper">
      <h3 class="kxi-text-normal kxi-text-600">{{ t('configuration') }}</h3>
      <p>{{ analysisItem.displayData.configName }}</p>
      <p>{{ analysisItem.analysis.configId }}</p>
    </div>

    <div class="project-panel__content-item-wrapper">
      <h3 class="kxi-text-normal kxi-text-600">{{ t('id') }}</h3>
      <p>{{ analysisItem.analysis.name ?? analysisItem.analysis.analysisName }}</p>
    </div>

    <div class="project-panel__content-item-wrapper">
      <h3 class="kxi-text-normal kxi-text-600">{{ t('created-date') }}</h3>
      <p>{{ analysisItem.created | lexDate }}</p>
    </div>
    @if (analysisItem.displayData.origin) {
      <div class="project-panel__content-item-wrapper">
        <h3 class="kxi-text-normal kxi-text-600">{{ t('collection-origin') }}</h3>
        @for (origin of analysisItem.displayData.origin; track origin) {
          <p>{{ origin }}</p>
        }
        @if (!analysisItem.displayData.origin[0]) {
          <p>{{ t('unspecified') }}</p>
        }
      </div>
    }

    @if (!analysisItem.isSocial) {
      <ng-container
        *ngTemplateOutlet="tActiveUse; context: { $implicit: analysisItem.displayData }"
      ></ng-container>
    }
    <ng-container *ngTemplateOutlet="tLabels; context: { $implicit: analysisItem }"></ng-container>
  </ng-template>

  <ng-template #tKeyDriverAnalysis let-analysisItem>
    <div class="project-panel__content-item-wrapper">
      <h3 class="kxi-text-normal kxi-text-600">{{ t('total-doc-count') }}</h3>
      <p>{{ analysisItem.displayData.kdaLabels?.docCount | number }}</p>
    </div>

    <div class="project-panel__content-item-wrapper">
      <h3 class="kxi-text-normal kxi-text-600">{{ t('created-date') }}</h3>
      <p>{{ analysisItem.created | lexDate }}</p>
    </div>

    @if (analysisItem.displayData.kdaLabels) {
      <div class="project-panel__content-item-wrapper">
        <h3 class="kxi-text-normal kxi-text-600">{{ t('dependent') }}</h3>
        <p>{{ analysisItem.displayData.kdaLabels.dependentColumn }}</p>
      </div>

      <div class="project-panel__content-item-wrapper">
        <h3 class="kxi-text-normal kxi-text-600">{{ t('drivers') }}</h3>
        @for (driver of analysisItem.displayData.kdaLabels.driverColumns; track driver) {
          <p>
            {{ driver }}
          </p>
        }
      </div>

      <div class="project-panel__content-item-wrapper">
        <h3 class="kxi-text-normal kxi-text-600">{{ t('hierarchy') }}</h3>
        <p>
          {{ analysisItem.displayData.kdaLabels.breakdownColumn ?? t('none') }}
        </p>
      </div>

      <div class="project-panel__content-item-wrapper">
        <h3 class="kxi-text-normal kxi-text-600">{{ t('weighting') }}</h3>
        <p>
          {{
            analysisItem.displayData.kdaLabels.weightField
              ? analysisItem.displayData.kdaLabels.weightField
              : t('no-weight-applied')
          }}
        </p>
      </div>
    }

    <ng-container
      *ngTemplateOutlet="
        tKDAFilters;
        context: {
          filters: analysisItem.displayData.kdaLabels?.widgetFilters,
          title: t('widget-filters'),
        }
      "
    ></ng-container>

    <ng-container
      *ngTemplateOutlet="
        tKDAFilters;
        context: {
          filters: analysisItem.displayData.kdaLabels?.comparisonFilters,
          title: t('comparison-filters'),
          type: 'comparison',
        }
      "
    ></ng-container>

    <ng-container
      *ngTemplateOutlet="
        tKDAFilters;
        context: {
          filters: analysisItem.displayData.kdaLabels?.widgetGroupFilters,
          title: t('widget-group-filters'),
        }
      "
    ></ng-container>

    @if (!analysisItem.isSocial) {
      <ng-container
        *ngTemplateOutlet="tActiveUse; context: { $implicit: analysisItem.displayData }"
      ></ng-container>
    }

    <ng-container *ngTemplateOutlet="tLabels; context: { $implicit: analysisItem }"></ng-container>
  </ng-template>

  <ng-template #tDashboard let-dashboardItem>
    @if (!isExecutive) {
      <div class="project-panel__content-item-wrapper">
        <div class="project-panel__content-pill-header-wrapper">
          <h3 class="project-panel__content-pill-header">
            {{ t('data-sources') }}
          </h3>
          <xi-default-button
            fill="link"
            [text]="t('rules') + '(' + dashboardItem.displayData.dashboardFilterCount + ')'"
            [hasAnimation]="false"
            (onClick)="onOpenDashboardRulesModal()"
          ></xi-default-button>
        </div>
        <xi-accordion>
          <div slot="children">
            <xi-accordion-item
              [title]="
                t('in-use') + ' (' + (dashboardItem.displayData.inUseDataSources?.length || 0) + ')'
              "
              [noPadding]="true"
              [open]="true"
            >
              <div slot="children">
                @for (ds of dashboardItem.displayData.inUseDataSources; track ds.id) {
                  <ng-container
                    *ngTemplateOutlet="tDashboardDataSources; context: { $implicit: ds }"
                  ></ng-container>
                }
              </div>
            </xi-accordion-item>

            <xi-accordion-item
              [title]="
                t('available') +
                ' (' +
                (dashboardItem.displayData.availableDataSources?.length || 0) +
                ') '
              "
              [noPadding]="true"
            >
              <div slot="children">
                @for (ds of dashboardItem.displayData.availableDataSources; track ds.id) {
                  <ng-container
                    *ngTemplateOutlet="tDashboardDataSources; context: { $implicit: ds }"
                  ></ng-container>
                }
              </div>
            </xi-accordion-item>
          </div>
        </xi-accordion>
      </div>
    }

    @if (dashboardItem.displayData.aliases?.length) {
      <div class="project-panel__content-item-wrapper">
        <h3 class="kxi-text-normal kxi-text-600">
          {{ t('aliases') }} ({{ dashboardItem.displayData.aliases.length }})
        </h3>

        <div class="project-panel__alias-list-wrapper">
          @for (alias of dashboardItem.displayData.aliases; track alias) {
            <p class="project-panel__alias-list-name">{{ alias.name }}</p>

            <xi-icon-button
              icon="copy"
              [tooltip]="{ text: t('copy-alias'), direction: 5 }"
              (onClick)="onCopyAlias(alias)"
            ></xi-icon-button>
          }
        </div>
      </div>
    }

    <ng-container *ngTemplateOutlet="tLabels; context: { $implicit: dashboardItem }"></ng-container>
  </ng-template>

  <ng-template #tDashboardDataSources let-ds>
    <div
      [matTooltip]="ds.filteredOutActiveUse ? t('excluded-data-source') : ds.displayName"
      (click)="onDataSourceClicked(ds)"
      class="project-panel__pill"
      [ngClass]="{ 'filtered-out': ds.filteredOutActiveUse }"
    >
      <xi-icon
        [icon]="ds.filteredOutActiveUse ? 'warning' : ds.icon"
        [color]="ds.color"
        [contrast]="ds.filteredOutActiveUse ? 'high' : 'normal'"
        iconSize="normal"
      ></xi-icon>
      <span>{{ ds.displayName | truncateEnd: 25 }}</span>
    </div>
  </ng-template>

  <ng-template #tActiveUse let-item>
    <div class="project-panel__active-use-wrapper">
      @if (!activeUseLoading) {
        <div class="project-panel__content-item-wrapper">
          @if (item.activeUse.length > 0 || item.hiddenDashboardCount > 0) {
            <h3 class="project-panel__content-pill-header">
              {{ t('active-use') }} ({{ item.activeUse.length + (item.hiddenDashboardCount || 0) }})
            </h3>
            @for (activeItem of item.activeUse; track activeItem) {
              <div
                [matTooltip]="activeItem.displayName"
                (click)="onActiveUseClicked(activeItem)"
                class="project-panel__pill"
              >
                <xi-icon
                  [icon]="icons[activeItem.activeUseType].icon"
                  [color]="icons[activeItem.activeUseType].color"
                  iconSize="normal"
                ></xi-icon>
                <span>{{ activeItem.displayName | truncateEnd: 25 }}</span>
              </div>
            }

            @if (item.hiddenDashboardCount > 0) {
              <div class="project-panel__active-use-hidden-dashboards">
                <p class="kxi-text-small">
                  {{ item.hiddenDashboardCount }} {{ t('hidden-dashboards') }}
                </p>
                <xi-icon
                  icon="question_mark"
                  [tooltipProps]="{
                    text: t('hidden-dashboards-tooltip'),
                  }"
                ></xi-icon>
              </div>
            }
          } @else {
            <h3 class="kxi-text-normal kxi-text-600">
              {{ t('active-use') }}
            </h3>
            <p class="kxi-text-small">{{ t('none') }}</p>
          }
        </div>
      }

      @if (activeUseLoading) {
        <div class="xi-loader-container__inline-overlay">
          <xi-animation
            [height]="75"
            [width]="75"
            [loop]="true"
            animation="cx_loader"
          ></xi-animation>
        </div>
      }
    </div>
  </ng-template>

  <ng-template #tKDAFilters let-title="title" let-filters="filters" let-type="type">
    <div class="project-panel__content-item-wrapper">
      <h3 class="kxi-text-normal kxi-text-600">{{ title }}</h3>
      @if (!filters) {
        <p class="kxi-text-small">{{ t('none') }}</p>
      }

      @if (filters) {
        @if (filters.andFilters.length > 0) {
          @if (type !== 'comparison') {
            <h4 class="kxi-text-small kxi-text-600">{{ t('all') }}</h4>
          }
          @for (f of filters.andFilters; track f) {
            <p class="kxi-text-small">{{ f }}</p>
          }
        }

        @if (filters?.orFilters.length > 0) {
          <h4 class="kxi-text-small kxi-text-600">{{ t('any') }}</h4>
          @for (f of filters.orFilters; track f) {
            <p class="kxi-text-small">{{ f }}</p>
          }
        }
        @if (filters?.notFilters.length > 0) {
          <h4 class="kxi-text-small kxi-text-600">{{ t('none') }}</h4>
          @for (f of filters.notFilters; track f) {
            <p class="kxi-text-small">{{ f }}</p>
          }
        }
      }
    </div>
  </ng-template>

  <ng-template #tLabels let-item>
    <div class="project-panel__content-item-wrapper">
      <h3 class="kxi-text-normal kxi-text-600">
        {{ t('labels-n', { count: item.labels.length }) }}
      </h3>

      <app-item-labels
        [item]="item | mapProjectItemToLabel"
        [isReadOnly]="isReadOnly"
        (labelUpdated)="onLabelUpdated($event, item)"
        (labelApplied)="onLabelApplied($event, item)"
        (labelRemoved)="onLabelRemoved($event, item)"
      ></app-item-labels>
    </div>
  </ng-template>
</ng-container>

@if (isNotesEditorOpen) {
  <app-lex-xi-modal
    [isVisible]="isNotesEditorOpen"
    maxWidth="600px"
    (close)="onCloseNotesEditor(null)"
  >
    <ng-template appXIModalContent>
      @if (isNotesEditorOpen) {
        <app-notes-editor [notes]="note" (close)="onCloseNotesEditor($event)"></app-notes-editor>
      }
    </ng-template>
  </app-lex-xi-modal>
}
@if (dashboardRulesModalOpen) {
  <app-lex-xi-modal
    [isVisible]="dashboardRulesModalOpen"
    [canBackdropClose]="false"
    maxWidth="1200px"
    minHeight="80vh"
    (close)="onCloseDashboardRulesModal(null)"
  >
    <ng-template appXIModalContent>
      @if (dashboardRulesModalOpen) {
        <app-dashboard-datasource-rules-modal
          [session]="session"
          [projectId]="project.id"
          [dashboard]="selectedItem.dashboard"
          [dataSources]="dataSources"
          [isReadOnly]="isReadOnly"
          (modalClosed)="onCloseDashboardRulesModal($event)"
        ></app-dashboard-datasource-rules-modal>
      }
    </ng-template>
  </app-lex-xi-modal>
}
